/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useCallback, useRef, createContext, useContext, type ReactNode } from "react"
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/components/auth-context"
import { differenceInSeconds } from "date-fns"
import { ZombieTimerRecoveryDialog } from "@/components/dialogs/zombie-timer-recovery-dialog"
import { audioService } from "@/lib/audio-utils"
import { sendDesktopNotification } from "@/lib/desktop-notifications"
import { toast } from "sonner"
import { Zap } from "lucide-react"
import { useTimerSettings } from "@/hooks/use-timer-settings.tsx"
import { offlineDB } from "@/lib/offline-db"
import { useActiveTimeEntryQuery, useClockInMutation, useClockOutMutation, useTogglePauseMutation } from "@/hooks/queries/use-time-entries"
import { useHaptics } from "@/hooks/use-haptics"

import type { TimeEntry, Pause } from '@/types/time-tracking'
export type { TimeEntry, Pause } from '@/types/time-tracking'

function useTimeTrackingInternal() {
    const { user } = useAuth()
    const { data: queryData, isLoading, refetch: refetchActiveEntry } = useActiveTimeEntryQuery()
    const { mutateAsync: clockInMutation } = useClockInMutation()
    const { mutateAsync: clockOutMutation } = useClockOutMutation()
    const { mutateAsync: togglePauseMutation } = useTogglePauseMutation()

    const { settings } = useTimerSettings()
    const { impactLight, impactMedium, notifySuccess, notifyError, vibrate } = useHaptics()

    const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null)
    const [elapsedTime, setElapsedTime] = useState(0)
    const [pauses, setPauses] = useState<Pause[]>([])
    const [zombieEntry, setZombieEntry] = useState<TimeEntry | null>(null)
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

    // Función de cálculo pura para evitar loops de dependencia
    const getCalculatedSeconds = useCallback((entry: TimeEntry, currentPauses: Pause[]) => {
        if (!entry) return 0
        const start = new Date(entry.clock_in)
        const now = new Date()

        let completedPauseSeconds = 0
        let activePauseRecord: Pause | null = null

        currentPauses.forEach(p => {
            if (p.end_time) {
                completedPauseSeconds += differenceInSeconds(new Date(p.end_time), new Date(p.start_time))
            } else {
                activePauseRecord = p
            }
        })

        if (entry.status === 'paused') {
            if (activePauseRecord) {
                const pauseStart = new Date((activePauseRecord as Pause).start_time)
                const totalAtPause = differenceInSeconds(pauseStart, start)
                return Math.max(0, totalAtPause - completedPauseSeconds)
            }
            return elapsedTime // Mantener el último si no hay registro aún
        } else {
            const totalSeconds = differenceInSeconds(now, start)
            let totalPauseSeconds = completedPauseSeconds
            if (activePauseRecord) {
                totalPauseSeconds += differenceInSeconds(now, new Date((activePauseRecord as Pause).start_time))
            }
            return Math.max(0, totalSeconds - totalPauseSeconds)
        }
    }, [elapsedTime])

    const updateTimer = useCallback((entry: TimeEntry | null, currentPauses: Pause[]) => {
        if (!entry) {
            setElapsedTime(0)
            return
        }
        const seconds = getCalculatedSeconds(entry, currentPauses)
        setElapsedTime(seconds)
    }, [getCalculatedSeconds])

    const fetchActiveEntry = useCallback(async () => {
        await refetchActiveEntry()
    }, [refetchActiveEntry])

    useEffect(() => {
        if (queryData) {
            if (queryData.zombie) {
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setZombieEntry(queryData.zombie)
            } else {
                setZombieEntry(null)
                setActiveEntry(queryData.entry)
                setPauses(queryData.pauses)
                updateTimer(queryData.entry, queryData.pauses)
            }
        }
    }, [queryData, updateTimer])

    useEffect(() => {
        if (activeEntry?.status === "active") {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            updateTimer(activeEntry, pauses)
            intervalRef.current = setInterval(() => {
                updateTimer(activeEntry, pauses)
            }, 1000)
        } else {
            if (activeEntry) updateTimer(activeEntry, pauses)
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
        return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
    }, [activeEntry, pauses, updateTimer])

    // Actualiza el título de la pestaña del navegador con el tiempo transcurrido.
    // Esto permite al usuario ver el estado del timer sin tener la pestaña activa.
    useEffect(() => {
        if (!activeEntry) {
            document.title = "Tempora"
            return
        }

        let displaySeconds = elapsedTime;
        let isOvertime = false;

        if (settings.isPomodoroEnabled) {
            const limit = (settings.pomodoroDuration || 25) * 60;
            if (elapsedTime > limit) {
                isOvertime = true;
                displaySeconds = elapsedTime - limit;
            } else {
                displaySeconds = limit - elapsedTime;
            }
        }

        const hours = Math.floor(displaySeconds / 3600)
        const minutes = Math.floor((displaySeconds % 3600) / 60)
        const seconds = displaySeconds % 60

        let formattedTime = [
            hours > 0 ? String(hours).padStart(2, "0") : null,
            String(minutes).padStart(2, "0"),
            String(seconds).padStart(2, "0"),
        ].filter(Boolean).join(":")

        if (settings.isPomodoroEnabled && hours === 0) {
            formattedTime = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
        }

        if (isOvertime) formattedTime = "+" + formattedTime;

        const taskName = activeEntry.task?.name ?? activeEntry.tasks?.name
        const projectName = activeEntry.project?.name ?? activeEntry.projects?.name
        const contextLabel = taskName ?? projectName ?? "Sesión activa"

        const statusEmoji = activeEntry.status === "paused" ? "⏸" : "⏱"
        document.title = `${statusEmoji} ${formattedTime} · ${contextLabel} — Tempora`

        return () => { document.title = "Tempora" }
    }, [activeEntry, elapsedTime, settings.isPomodoroEnabled, settings.pomodoroDuration])

    useEffect(() => {
        if (!user) return
        const channel = supabase
            .channel(`time-tracking-${user.id}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'time_entries',
                filter: `user_id=eq.${user.id}`
            }, () => fetchActiveEntry())
            .subscribe()
        return () => { supabase.removeChannel(channel) }
    }, [user, fetchActiveEntry])

    const syncOfflineEntries = useCallback(async () => {
        if (!user) return
        const unsynced = await offlineDB.timeEntries.where('synced').equals(0).toArray()
        if (unsynced.length === 0) return
        for (const entry of unsynced) {
            try {
                const { error } = await supabase.from("time_entries").upsert({
                    user_id: entry.user_id, organization_id: entry.organization_id, project_id: entry.project_id, task_id: entry.task_id,
                    clock_in: entry.clock_in, clock_out: entry.clock_out, status: entry.status, total_hours: entry.total_hours, date: entry.clock_in.split('T')[0]
                })
                if (!error) await offlineDB.timeEntries.update(entry.id!, { synced: true })
            } catch (err: unknown) {
                console.error("Failed to sync entry:", err)
            }
        }
        fetchActiveEntry()
    }, [user, fetchActiveEntry])

    useEffect(() => {
        const handleVisibilityChange = () => { if (document.visibilityState === 'visible') fetchActiveEntry() }
        const handleOnline = () => { void syncOfflineEntries() }
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('online', handleOnline)
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('online', handleOnline)
        }
    }, [fetchActiveEntry, syncOfflineEntries])

    // Moved to top of hook using useTimerSettings()

    useEffect(() => {
        const workAlertInterval = setInterval(() => {
            if (activeEntry?.status === 'active' && elapsedTime > 0) {
                const intervalSeconds = (settings.alertInterval || 30) * 60
                const elapsedMinutes = Math.floor(elapsedTime / 60)

                // Intervalo regular
                if (elapsedTime % intervalSeconds === 0) {
                    if (settings.soundsEnabled) audioService.playClockChime(settings.soundType)
                    impactLight()
                    if (settings.desktopNotificationsEnabled) {
                        sendDesktopNotification({ title: '🔔 Campana de control — Tempora', body: `Llevas ${elapsedMinutes} minutos trabajando. ¡Sigue así!`, tag: 'tempora-work-alert' })
                    }
                    toast.custom(() => (
                        <div className="bg-zinc-950 border border-primary/20 rounded-xl shadow-2xl shadow-primary/10 p-4 flex items-start gap-4 w-[356px]">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-primary"><Zap className="h-5 w-5" /></div>
                            <div className="flex-1 min-w-0"><h3 className="font-bold text-sm text-primary">¡Campana de control!</h3><p className="text-xs text-zinc-400 mt-1">Llevas {elapsedMinutes} minutos trabajando. Sigue así.</p></div>
                        </div>
                    ), { duration: 5000 })
                }

                // Alerta específica Pomodoro
                if (settings.isPomodoroEnabled && elapsedTime === (settings.pomodoroDuration || 25) * 60) {
                    if (settings.soundsEnabled) audioService.playSuccess(settings.soundType)
                    vibrate()
                    if (settings.desktopNotificationsEnabled) {
                        sendDesktopNotification({ title: '🍅 Pomodoro Completado', body: `¡Buen trabajo! Es hora de tomar tu descanso.`, tag: 'tempora-pomodoro-alert' })
                    }
                    toast.custom(() => (
                        <div className="bg-zinc-950 border border-rose-500/20 rounded-xl shadow-2xl shadow-rose-500/10 p-4 flex items-start gap-4 w-[356px]">
                            <div className="h-10 w-10 rounded-full bg-rose-500/10 flex items-center justify-center flex-shrink-0 text-rose-500 animate-bounce"><span className="text-xl">🍅</span></div>
                            <div className="flex-1 min-w-0"><h3 className="font-bold text-sm text-rose-500">¡Pomodoro Completado!</h3><p className="text-xs text-zinc-400 mt-1">Has finalizado tu intervalo de concentración profunda y puedes descansar un momento.</p></div>
                        </div>
                    ), { duration: 10000 })
                }
            }
        }, 1000)
        let idleReminderInterval: ReturnType<typeof setInterval> | null = null
        if (!activeEntry) {
            idleReminderInterval = setInterval(() => {
                if (document.visibilityState === 'visible') {
                    if (settings.soundsEnabled) audioService.playReminder(settings.soundType)
                    toast.custom((t) => (
                        <div className="bg-zinc-900 border border-amber-500/20 rounded-xl shadow-2xl shadow-amber-500/10 p-4 flex items-start gap-4 w-[356px]">
                            <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0 text-amber-500 animate-pulse"><span className="text-xl">⏸️</span></div>
                            <div className="flex-1"><h3 className="font-bold text-sm text-amber-500">Recordatorio: Cronómetro detenido</h3><p className="text-xs text-zinc-400 mt-1">¿Has olvidado iniciar tu sesión de trabajo?</p><button onClick={() => toast.dismiss(t)} className="mt-2 text-[10px] text-zinc-500 underline">Descartar</button></div>
                        </div>
                    ), { duration: 10000 })
                } else if (settings.desktopNotificationsEnabled) {
                    sendDesktopNotification({ title: '⏸️ Cronómetro detenido — Tempora', body: '¿Has olvidado iniciar tu sesión de trabajo?', tag: 'tempora-idle-reminder' })
                }
            }, 15 * 60 * 1000)
        }
        return () => {
            clearInterval(workAlertInterval)
            if (idleReminderInterval) clearInterval(idleReminderInterval)
        }
    }, [activeEntry, elapsedTime, settings, impactLight, vibrate])

    const clockIn = async (projectId?: string, taskId?: string) => {
        try {
            const result = await clockInMutation({ projectId, taskId, activeEntry })
            impactMedium()
            if (result && 'offline' in result && result.offline) {
                setActiveEntry(result as unknown as TimeEntry)
                toast.warning("Modo Offline: Sesión iniciada localmente.")
            }
        } catch (error: unknown) {
            console.error("Error clocking in:", error)
        }
    }

    const clockOut = async () => {
        if (!activeEntry) return

        try {
            await clockOutMutation({ activeEntry, pauses })
            notifySuccess()
            audioService.playSuccess(settings.soundType)
        } catch (_error: unknown) {
            notifyError()
            toast.warning("Sesión finalizada localmente.")
        }

        setActiveEntry(null)
        setElapsedTime(0)
        setPauses([])
    }

    const togglePause = async () => {
        if (!activeEntry) return
        const now = new Date().toISOString()
        const newStatus = activeEntry.status === "active" ? "paused" : "active"
        impactLight()

        // Optimistic UI updates
        if (newStatus === "paused") {
            const tempPause: Pause = { id: 'temp-' + Date.now(), time_entry_id: activeEntry.id, start_time: now, type: 'break', end_time: null }
            setPauses(prev => [tempPause, ...prev])
        } else {
            setPauses(prev => prev.map(p => p.end_time === null ? { ...p, end_time: now } : p))
        }

        setActiveEntry({ ...activeEntry, status: newStatus })

        try {
            await togglePauseMutation({ activeEntry })
        } catch (e) {
            console.error(e)
            // Rollback Podría agregarse aquí
        }
    }

    const keepZombieTime = () => { if (zombieEntry) { setActiveEntry(zombieEntry); updateTimer(zombieEntry, []); setZombieEntry(null) } }
    const fixZombieTime = async (newEndTime: string) => {
        if (!zombieEntry) return
        const start = new Date(zombieEntry.clock_in); const end = new Date(newEndTime)
        const totalHours = differenceInSeconds(end, start) / 3600
        if (totalHours < 0) { alert("La hora de fin no puede ser anterior al inicio."); return }
        const { error } = await supabase.from("time_entries").update({ clock_out: newEndTime, status: "completed", total_hours: Number(totalHours.toFixed(2)) }).eq("id", zombieEntry.id)
        if (!error) { setZombieEntry(null); fetchActiveEntry() }
    }

    return { activeEntry, isLoading, elapsedTime, zombieEntry, keepZombieTime, fixZombieTime, clockIn, clockOut, togglePause, syncOfflineEntries, refetch: fetchActiveEntry }
}

type TimeTrackingContextType = ReturnType<typeof useTimeTrackingInternal>
const TimeTrackingContext = createContext<TimeTrackingContextType | null>(null)

export function TimeTrackingProvider({ children }: { children: ReactNode }) {
    const value = useTimeTrackingInternal()
    return (
        <TimeTrackingContext.Provider value={value}>
            {children}
            <ZombieTimerRecoveryDialog open={!!value.zombieEntry} entry={value.zombieEntry} onKeep={value.keepZombieTime} onFix={value.fixZombieTime} />
        </TimeTrackingContext.Provider>
    )
}

export function useTimeTracking(): TimeTrackingContextType {
    const context = useContext(TimeTrackingContext)
    if (!context) throw new Error("useTimeTracking must be used within a TimeTrackingProvider")
    return context
}
